import { Header, Generator, Gallery } from './components'
import { useConfig } from './hooks/useConfig'
import { useGallery } from './hooks/useGallery'

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 border-4 border-exness-yellow border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-500 font-medium">Loading configuration...</p>
      </div>
    </div>
  )
}

function ErrorScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Configuration Error</h2>
        <p className="text-gray-500 mb-6">{message}</p>
        <button
          onClick={onRetry}
          className="px-6 py-3 bg-exness-yellow hover:bg-yellow-400 text-exness-dark font-semibold rounded-xl transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  )
}

export default function App() {
  const { config, loading: configLoading, error: configError, reload: reloadConfig } = useConfig()
  const { images, loading: galleryLoading, addImage } = useGallery()

  if (configLoading) {
    return <LoadingScreen />
  }

  if (configError || !config) {
    return <ErrorScreen message={configError || 'Failed to load configuration'} onRetry={reloadConfig} />
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Header logoUrl={config.assets?.logoPath} />
      
      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-exness-dark mb-3">
            {config.ui.title || 'Icon Generator'}
          </h1>
          <p className="text-gray-500 max-w-lg mx-auto">
            Generate brand-consistent icons with AI. Select your style, pick a color, and describe what you need.
          </p>
        </div>

        {/* Generator Controls */}
        <Generator config={config} onNewImage={addImage} />

        {/* Gallery */}
        <div className="mt-16 border-t border-gray-100 pt-8">
          <Gallery 
            images={images} 
            loading={galleryLoading}
            title={config.ui.galleryTitle}
            downloadText={config.ui.downloadButtonText}
            closeText={config.ui.closeButtonText}
          />
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-gray-400 text-sm">
        <p>Powered by OpenAI Image API</p>
      </footer>
    </div>
  )
}
